import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@swyftx/aviary/atoms/Button';
import { Modal } from '@swyftx/aviary/atoms/Modal';
import { Notification } from '@swyftx/aviary/atoms/Notification';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@swyftx/aviary/atoms/Tabs';
import { Body } from '@swyftx/aviary/atoms/Typography';

import { FiatIdEnum } from '@shared/enums';
import { assetService } from '@shared/services';

import { useForm } from 'react-hook-form';
import { BankTransfers } from 'src/routes/Onboarding/components/OnboardingSteps/depositFunds/DepositFundsBankTransfers';
import { PayID } from 'src/routes/Onboarding/components/OnboardingSteps/depositFunds/DepositFundsPayID';

import { LoansStepContainer } from './LoansStepContainer';
import { useLoanApplicationService } from '../../xstate/LoanApplication.context';

const NominateBankAccountStep: React.FC = () => {
  const { t } = useTranslation('onboarding');
  const loanApplicationService = useLoanApplicationService();

  const [open, setOpen] = React.useState<boolean>(false);

  const {
    handleSubmit,
    formState: { isValid },
  } = useForm({
    mode: 'onChange',
  });

  const onSubmit = () => {
    setOpen(true);
  };

  const asset = assetService.getAsset(FiatIdEnum.AUD);

  if (!asset) {
    // TODO: what do?
    return null;
  }

  return (
    <>
      <LoansStepContainer
        customTitle='Nominate your bank account'
        onSubmit={handleSubmit(onSubmit)}
        submitDisabled={!isValid}
      >
        <main className='flex flex-col gap-32'>
          <Body>Deposit $1 to verify where you&apos;d like to receive your loan funds.</Body>

          <Notification severity='info' title='Only deposit from a bank account in your name'>
            Depositing from a bank account that does not match the legal name provided on your Swyftx account will not
            be considered. Deposits from joint accounts may be accepted.
          </Notification>

          <Tabs defaultValue='bankTransfers'>
            <TabsList className='grid w-full grid-cols-2'>
              <TabsTrigger value='bankTransfers'>{t('steps.depositFunds.option.bankTransfers.title')}</TabsTrigger>
              <TabsTrigger value='payID'>{t('steps.depositFunds.option.payID.title')}</TabsTrigger>
            </TabsList>
            <TabsContent value='bankTransfers'>
              <BankTransfers asset={asset} disableHeading depositAmount='$1.00' disableBelowFold />
            </TabsContent>
            <TabsContent value='payID'>
              <PayID asset={asset} disableHeading depositAmount='$1.00' disableBelowFold />
            </TabsContent>
          </Tabs>
        </main>
      </LoansStepContainer>

      <Modal onOpenChange={(o) => setOpen(o || false)} open={open} className='max-w-md'>
        <div className='space-y-32 p-24'>
          <h2 className='mb-1 text-[1.125rem] text-lg'>Have you made a $1 deposit?</h2>

          <div>
            <Body weight='bold'>Why is this needed?</Body>
            <Body>
              The bank account you deposit from is where we&apos;ll send your loan funds. Depositing from this account
              verifies you can access these funds once sent.
            </Body>
          </div>

          <div>
            <Body weight='bold'>Do I really need to deposit?</Body>
            <Body>
              Yes. We cannot start the review process without a nominated bank account attached to your application.
            </Body>
          </div>

          <div className='flex flex-row justify-end space-x-12'>
            <Button
              size='lg'
              variant='outlined'
              onClick={() => {
                setOpen(false);
              }}
            >
              No, go back
            </Button>

            <Button
              size='lg'
              onClick={() => {
                setOpen(false);
                loanApplicationService.send({ type: 'NEXT' });
              }}
            >
              Yes, I have deposited
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export { NominateBankAccountStep };
